import { type FC } from 'react';
import { useSelector } from 'react-redux';

import { type RootState } from '@editor2/reducers/root';
import ChangesDiff from '../common/ChangesDiff';
import { type Page } from '@editor2/types/page';
import { getEntityById, getInitialIndex, getPageByElementId, getPageTitle } from '../helpers';
import { elementHeader } from '../styles';
import {
	type Timeline,
	type GeoMap,
	type MCQuestion,
	type ResponseBoard,
	type SAQuestion,
	type MCQuestionPool,
	type EocCheckinChatbot,
	ElementType
} from '@editor2/types/pageElements';
import ChoicesDiff from './ChoicesDiff';
import { sanitizeValidations } from '@editor2/services/elementValidationsService';
import { getElementHumanName } from '@editor2/services/elementService';
import TimelineDataDiff from './TimelineDataDiff';
import GeoMapDataDiff from './GeoMapDataDiff';
import PoolQuestionsDiff from './PoolQuestionsDiff';
import KeyConceptsDiff from './KeyConceptsDiff';

interface Props {
	courseId: number;
	elementChanges: any;
	index: number;
	initialPage?: Page;
}

const ElementChangesDiff: FC<Props> = (props) => {
	const { courseId, elementChanges, index, initialPage } = props;

	const { id, ...attributeChanges } = elementChanges;

	const { elements, initialCourse, pages, config } = useSelector(
		(state: RootState) => state.courses[courseId]
	);

	const initialElement = getEntityById(initialCourse.elements, id);
	const currentElement =
		getEntityById(elements, id) || elements.entities[attributeChanges.family_id];
	const initialIndex = initialPage?.elements
		? getInitialIndex(initialPage.elements, initialElement?.family_id)
		: -1;

	if (Object.keys(elementChanges).length === 1 && index === initialIndex) return <></>;

	const getParentPage = () => {
		return (
			getPageByElementId(initialCourse.pages, currentElement.family_id) ||
			getPageByElementId(pages, currentElement.family_id)
		);
	};

	const getParentName = () => {
		const initPage = getParentPage();
		return initPage ? getPageTitle(initPage) : '';
	};

	const parentPageType = getParentPage()?.page_type;

	const getElementAttributesChanges = (changes) => {
		return (
			<ChangesDiff
				title={getElementHumanName(config, parentPageType, currentElement)}
				index={index}
				initialIndex={initialIndex}
				initial={initialElement}
				attributeChanges={changes}
				parentName={getParentName()}
				styles={elementHeader}
			/>
		);
	};

	return (
		<>
			{(() => {
				switch (currentElement.type) {
					case ElementType.MCQuestion: {
						const { id, choices, ...attributeChanges } = elementChanges;

						return (
							<>
								{getElementAttributesChanges(attributeChanges)}
								<ChoicesDiff
									initialElement={initialElement as MCQuestion}
									currentElement={currentElement}
									choices={choices}
								/>
							</>
						);
					}

					case ElementType.MCQuestionPool: {
						const { id, questions, ...attributeChanges } = elementChanges;

						return (
							<>
								{getElementAttributesChanges(attributeChanges)}
								<PoolQuestionsDiff
									initialElement={initialElement as MCQuestionPool}
									currentElement={currentElement}
									questions={questions}
								/>
							</>
						);
					}

					case ElementType.Timeline: {
						const { id, data, ...attributeChanges } = elementChanges;

						return (
							<>
								{getElementAttributesChanges(attributeChanges)}
								{data && (
									<TimelineDataDiff
										initialElement={initialElement as Timeline}
										currentElement={currentElement}
										data={data}
									/>
								)}
							</>
						);
					}

					case ElementType.EocCheckinChatbot: {
						const { id, ...attributeChanges } = elementChanges;
						return (
							<KeyConceptsDiff
								title={getElementHumanName(config, parentPageType, currentElement)}
								index={index}
								initialIndex={initialIndex}
								initialElement={initialElement as EocCheckinChatbot}
								currentElement={currentElement}
								attributeChanges={attributeChanges}
								parentName={getParentName()}
							/>
						);
					}

					case ElementType.GeoMap: {
						const { id, ...attributeChanges } = elementChanges;
						return (
							<GeoMapDataDiff
								title={getElementHumanName(config, parentPageType, currentElement)}
								index={index}
								initialIndex={initialIndex}
								initialElement={initialElement as GeoMap}
								currentElement={currentElement}
								attributeChanges={attributeChanges}
								parentName={getParentName()}
							/>
						);
					}

					case ElementType.YourTurn:
					case ElementType.SAQuestion: {
						const { id, validations, ...attributeChanges } = elementChanges;

						return (
							<>
								<ChangesDiff
									title={getElementHumanName(config, parentPageType, currentElement)}
									index={index}
									initialIndex={initialIndex}
									initial={{
										...initialElement,
										...(initialElement && {
											validations: sanitizeValidations(
												(initialElement as ResponseBoard | SAQuestion).validations
											)
										})
									}}
									attributeChanges={{
										...attributeChanges,
										...(validations && { validations: sanitizeValidations(validations) })
									}}
									parentName={getParentName()}
									styles={elementHeader}
								/>
							</>
						);
					}

					default:
						return <>{getElementAttributesChanges(attributeChanges)}</>;
				}
			})()}
		</>
	);
};

export default ElementChangesDiff;
